import axios from "axios";
import { backend_url } from "@/constants";
import { AXIOS_CONFIG } from "@/utils/api";

export interface UserPreferences {
  /**
   * The status to apply once a client has been OCR'd
   */
  ocrAutoStatus: string | null;
  /**
   * The status to apply after client has been one-clicked.
   */
  oneClickAutoStatus: string | null;
  /**
   * Whether to hide the credits confirmation menu.
   */
  hideCreditConfirmation: boolean;
  /**
   * Whether to hide the create client welcome dialog.
   */
  hideCreateClientWelcome: boolean;
  /**
   * Automatically select checkbox for states with no state income tax
   */
  exportAutoSelectNoStateTax: boolean;
  /**
   * Automatically answer due diligence questions
   */
  exportAnswerDueDiligence: boolean;
  /**
   * Automatically complete third party designator section on state forms
   */
  exportAnswerThirdPartyDesignator: boolean;
  /**
   * Use the last 4 digits of client SSN as the default client PIN for tax preparation
   */
  useSsnLast4AsClientPin: boolean;
}

export const DEFAULT_SETTINGS: UserPreferences = {
  ocrAutoStatus: null,
  oneClickAutoStatus: null,
  hideCreditConfirmation: false,
  hideCreateClientWelcome: false,
  exportAutoSelectNoStateTax: false,
  exportAnswerDueDiligence: false,
  exportAnswerThirdPartyDesignator: false,
  useSsnLast4AsClientPin: false,
};

interface UserPreferencesResponse {
  settings: UserPreferences;
}

interface UserPreferencesResponseContainer {
  status: string;
  message: string;
  content: UserPreferencesResponse;
}

/**
 * Fetches the current user preferences for the authenticated user.
 * @returns Promise<{settings: UserPreferences}>
 */
export const getExtensionSettings =
  async (): Promise<UserPreferencesResponse> => {
    try {
      const response = await axios.get<UserPreferencesResponseContainer>(
        `${backend_url}/api/ext/settings/`,
        AXIOS_CONFIG
      );

      if (response.status === 200 && response.data.content) {
        const content = response.data.content;
        return content;
      }

      throw new Error("Invalid response format");
    } catch (error: any) {
      console.error("[UserPreferencesAPI] Failed to fetch settings:", error);
      console.error("[UserPreferencesAPI] Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      // Return default settings on error
      return {
        settings: DEFAULT_SETTINGS,
      };
    }
  };

/**
 * Updates user preferences for the authenticated user.
 * @param settings Partial user preferences to update
 * @returns Promise<{settings: UserPreferences}>
 */
export const updateExtensionSettings = async (
  settings: Partial<UserPreferences>
): Promise<{ settings: UserPreferences }> => {
  try {
    const response = await axios.patch(
      `${backend_url}/api/ext/settings/`,
      { settings },
      AXIOS_CONFIG
    );

    if (response.status === 200 && response.data.content) {
      return response.data.content;
    }

    throw new Error("Failed to update settings");
  } catch (error: any) {
    console.error("[UserPreferencesAPI] Failed to update settings:", error);
    throw error;
  }
};
